/**
 * @fileOverview The settings walkthrough: the one page the whole workspace is set up on, tab by tab.
 * Pure data.
 * See docs/view-help.md and docs/view-help/settings.md.
 */

import { FEATURE_SETS } from "@/services/featureSet"
import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/** Reading the working-time models — the Arbeitszeitmodelle tab needs it, next to the add-on. */
const VIEW_MODELS = [PERMISSIONS.VIEW_WORK_TIME_MODEL]

/** Reading the subscription — without it the Abonnement tab is not rendered at all. */
const VIEW_BILLING = [PERMISSIONS.VIEW_BILLING]

/** The whole archive, so only the Administrator role holds it — and only it sees the tab. */
const EXPORT_DATA = [PERMISSIONS.EXPORT_TENANT_DATA]

/** Changing roles, Administrator-only too. Without it a role opens read-only and there is nothing to discard. */
const EDIT_ROLES = [PERMISSIONS.EDIT_ROLES]

const TIME_TRACKING = [FEATURE_SETS.TIME_TRACKING]

const DOCUMENT_DESIGN = [FEATURE_SETS.DOCUMENT_DESIGN]

/** The Tagesmeldungen section of Allgemein is rendered only with the feature-set. */
const DAY_SUBMISSIONS = [FEATURE_SETS.DAY_SUBMISSIONS]

/** The route itself carries no permission (`router/routes/settings.js`): every user may open it. */
const onSettings = stepsOn("SettingsOverview")

/** The related view: a sample person's own page, where several of these settings are overridden. */
const onEmployee = stepsOn("EmployeeDetail", { at: { params: { employeeId: "demo-1" } } })

/**
 * The three pages that read what this one sets. Each reuses the drawer gate of the area it belongs
 * to, so the permission and add-on lists stay in `services/navigation.js`: the cost pages need one of
 * the two Projektkosten rights, Meine Zeiten only the Zeiterfassung add-on, and the planning board the
 * add-on plus `VIEW_PERSONNELPLANNING`.
 */
const onCosts = stepsOn("ProjectCostsForOne", {
  navEntry: "nav-costs",
  at: { params: { projectId: "p-nord" } },
})
const onMyTimes = stepsOn("TimeTrackingMy", { navEntry: "nav-timetracking" })
const onPlanning = stepsOn("PersonnelCalendar", {
  navEntry: "nav-personnelplanning",
  at: { params: { calendarId: "cal-fruehjahr" } },
})

/**
 * Either the subscription card or, for a workspace that is exempt from billing, the notice that
 * stands in its place. Naming both means the step survives whichever the reader's workspace has.
 */
const SUBSCRIPTION = ["settings-billing-plan", "settings-billing-exempt"]

/**
 * The settings: one page, nine tabs, everything on it valid for the whole workspace.
 *
 * **The tabs are walked with visible clicks**, and all of them belong to a single scene (`tabs`).
 * That is what makes every step reachable from the timeline: the player replays the earlier steps of
 * a scene at speed, so jumping onto a step of the Datenexport tab clicks its way along the strip
 * rather than pointing at a tab nobody selected. It is also why the tab steps are the one place a
 * scene's `enter` is empty — each tab step clicks its own tab.
 *
 * **The tab steps keep to the whole page** (`zoom: false`). The strip is 1191px wide in a 1280px
 * frame, so its last tabs sit partly behind the slide arrows, and zoomed onto one the reader would
 * read a clipped label. What a tab is for is the body it swaps in anyway, which only shows with the
 * page in view.
 *
 * Nothing is ever saved: the one "Speichern" at the foot is pointed at and explained, the "+" of the
 * absence kinds is never pressed (it posts at once), the logo's file picker is never opened, and the
 * role card is left by its ✕, which discards. The frame is sealed, so none of it could write — but a
 * save that visibly failed is not what a walkthrough should show.
 */
export const SETTINGS_TOUR: ViewHelpTour = {
  key: "settings",
  icon: "mdi-cog",
  routes: ["SettingsOverview"],
  // Both directions. Where a setting is overridden per person (the role and the employer's
  // contribution on a sample employee), and the three pages where a setting made here is visibly at
  // work: the contribution inside a project's cost to date, the first day of the week in Meine
  // Zeiten, and an absence kind's colour on the planning board.
  related: ["EmployeeDetail", "ProjectCostsForOne", "TimeTrackingMy", "PersonnelCalendar"],
  scenes: [
    // The whole walk across the strip. One scene rather than one per tab: with one each, a jump onto
    // a later tab's step would enter only that scene, and its tab — slid to the far end of a strip
    // that is wider than the frame — is not on screen to be clicked. As one scene, the earlier tab
    // clicks are replayed at speed first, and each of them slides the next tab into view.
    // Nothing to undo on the way out: picking a tab changes nothing outside this page, and the reader
    // watching the last tab explained should not see the page snap back to the first one.
    { key: "tabs", enter: [], exit: [] },
    // Max's page: the pointer presses "Unternehmens-relevant" as a step of its own, then the page
    // scrolls down to the field. One scene, so a jump to the field clicks the tab first, at speed.
    { key: "employee-record", enter: [], exit: [] },
    // The planning board, brought onto today first: it opens at the earliest montage date of its
    // projects, months before anybody is planned, where the employee lane holds no absence to show.
    { key: "planning-board", enter: [{ do: "showToday", target: "planning-today" }], exit: [] },
  ],
  steps: [
    // ── the page ─────────────────────────────────────────────────────────────
    onSettings("head", ["settings-head", "settings-meta"], "mdi-cog-outline", { placement: "bottom" }),
    // Explained before the tabs, because it is the one button three of them are saved by. It is
    // pointed at and never pressed.
    onSettings("save", "settings-save", "mdi-content-save-outline", { placement: "top" }),

    // ── Allgemein ────────────────────────────────────────────────────────────
    onSettings("tab-general", "tab-general", "mdi-tune", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-general" }],
    }),
    // The file picker is never opened — a native dialog would sit over the frame with nothing to
    // close it from the page.
    onSettings("logo", "settings-logo", "mdi-image-outline", { placement: "bottom", scene: "tabs" }),
    onSettings("currency", "settings-currency", "mdi-cash", { placement: "bottom", scene: "tabs" }),
    onSettings("weekstart", "settings-weekstart", "mdi-calendar-week", { placement: "bottom", scene: "tabs" }),
    onSettings("contribution", "settings-contribution", "mdi-percent-outline", { placement: "top", scene: "tabs" }),
    onSettings("daysubmission", "settings-daysubmission", "mdi-crosshairs-gps", {
      placement: "top",
      scene: "tabs",
      featureSets: DAY_SUBMISSIONS,
    }),
    onSettings("timeentry", "settings-timeentry", "mdi-map-marker-check-outline", {
      placement: "top",
      scene: "tabs",
      featureSets: TIME_TRACKING,
    }),
    onSettings("address", "settings-address", "mdi-map-marker-outline", { placement: "top", scene: "tabs" }),

    // ── Bezeichnungen ────────────────────────────────────────────────────────
    onSettings("tab-terminology", "tab-terminology", "mdi-format-letter-case", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-terminology" }],
    }),
    // Pointed at, never picked: a pick only changes the table and the sentence below it until the
    // save, and the save is the one thing a walkthrough does not do.
    onSettings("terminology-names", "settings-terminology", "mdi-rename-outline", {
      placement: "bottom",
      scene: "tabs",
    }),
    onSettings("terminology-preview", "settings-terminology-preview", "mdi-text-box-check-outline", {
      placement: "top",
      scene: "tabs",
    }),

    // ── Berechtigungen ───────────────────────────────────────────────────────
    onSettings("tab-rights", "tab-rights", "mdi-shield-account-outline", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-rights" }],
    }),
    onSettings("default-role", "settings-default-role", "mdi-account-plus-outline", {
      placement: "bottom",
      scene: "tabs",
    }),
    onSettings("role-list", "settings-role-list", "mdi-format-list-bulleted", { placement: "right", scene: "tabs" }),
    // The first role that can be opened — the Administrator's row is inert, since that role always
    // holds everything. The camera frames the panel the rights appear in while the pointer clicks.
    onSettings("role-rights", "settings-role-detail", "mdi-key-outline", {
      placement: "left",
      scene: "tabs",
      actions: [{ do: "click", target: "settings-role-row" }],
    }),
    // The tree as the region, not the group row: the point is the rights appearing under the group
    // the pointer opens, and the camera holds the whole tree while it does.
    onSettings("role-tree", "settings-right-tree", "mdi-file-tree-outline", {
      placement: "left",
      scene: "tabs",
      actions: [{ do: "click", target: "settings-right-group" }],
    }),
    // Pointed at, not pressed: pressing it would take the tree the step before it just opened off
    // the page as this step's text came on.
    onSettings("role-close", "settings-role-close", "mdi-close", {
      placement: "bottom",
      scene: "tabs",
      permissions: EDIT_ROLES,
    }),

    // ── Abwesenheitsarten ────────────────────────────────────────────────────
    onSettings("tab-absence", "tab-absence", "mdi-calendar-remove-outline", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-absence" }],
    }),
    onSettings("absence-default", "settings-absence-default", "mdi-palette-outline", {
      placement: "right",
      scene: "tabs",
    }),
    // A name is typed, and that is as far as it goes: the "+" posts the new kind at once, and the
    // colour sits behind the browser's own colour picker, which the frame has no way to close.
    onSettings("absence-custom", "settings-absence-custom", "mdi-plus-box-outline", {
      placement: "left",
      scene: "tabs",
      actions: [{ do: "type", target: "settings-absence-name", label: "help.tour.settings.typed.absence" }],
    }),

    // ── Benachrichtigungen ───────────────────────────────────────────────────
    onSettings("tab-notification", "tab-notification", "mdi-bell-outline", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-notification" }],
    }),
    onSettings("notify-levels", "settings-notify-levels", "mdi-alert-outline", { placement: "bottom", scene: "tabs" }),
    // Grouped by where the bell takes the reader, not one step per section: four sections about a
    // project's money lead to two screens, and eight steps on eight boxes of numbers would say less.
    onSettings("notify-invoicing", ["settings-notify-unbilled", "settings-notify-reports"], "mdi-file-alert-outline", {
      placement: "right",
      scene: "tabs",
    }),
    onSettings(
      "notify-costs",
      ["settings-notify-loss", "settings-notify-drift", "settings-notify-montage"],
      "mdi-chart-line",
      { placement: "right", scene: "tabs" }
    ),
    // One switch for all three time-tracking monitors, and only for a workspace with the add-on.
    onSettings("notify-time", "settings-notify-time", "mdi-clock-alert-outline", {
      placement: "right",
      scene: "tabs",
      featureSets: TIME_TRACKING,
    }),
    onSettings("notify-documents", ["settings-notify-documents", "settings-notify-email"], "mdi-file-clock-outline", {
      placement: "right",
      scene: "tabs",
    }),

    // ── Arbeitszeitmodelle ───────────────────────────────────────────────────
    //
    // One step, pointing at the list and saying whose feature it is. The models themselves — what
    // they compute and where it shows — are the time-tracking tour's Zusammenhänge chapter, and saying
    // it twice would mean saying it differently one day.
    onSettings("worktimemodel", "tt-model-list", "mdi-calendar-clock", {
      placement: "top",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-worktimemodel" }],
      featureSets: TIME_TRACKING,
      permissions: VIEW_MODELS,
    }),

    // ── PDF-Vorlagen ─────────────────────────────────────────────────────────
    //
    // Two steps in the page's order: the documents, then the Briefpapiere. The designing happens on the
    // editors' own pages, and their preview is a POST the sealed frame refuses, so the walkthrough does
    // not enter them.
    onSettings("pdf-templates", "settings-pdf-templates", "mdi-file-document-edit-outline", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-pdftemplates" }],
      featureSets: DOCUMENT_DESIGN,
    }),
    onSettings("pdf-letterheads", "settings-pdf-letterheads", "mdi-file-document-outline", {
      placement: "top",
      scene: "tabs",
      zoom: false,
      featureSets: DOCUMENT_DESIGN,
    }),

    // ── Abonnement ───────────────────────────────────────────────────────────
    onSettings("tab-billing", "tab-billing", "mdi-crown-outline", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-billing" }],
      permissions: VIEW_BILLING,
    }),
    onSettings("billing-plan", SUBSCRIPTION, "mdi-account-multiple-outline", {
      placement: "bottom",
      scene: "tabs",
      permissions: VIEW_BILLING,
    }),
    // Pointed at, never pressed: each button leaves the page for its own, and cancelling is a write.
    // Absent for an exempt workspace, which has no subscription to manage — the step then drops out.
    onSettings("billing-manage", "settings-billing-manage", "mdi-swap-horizontal", {
      placement: "bottom",
      scene: "tabs",
      permissions: VIEW_BILLING,
    }),

    // ── Datenexport ──────────────────────────────────────────────────────────
    onSettings("tab-dataexport", "tab-dataexport", "mdi-database-arrow-down-outline", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-dataexport" }],
      permissions: EXPORT_DATA,
    }),
    // Pointed at, never pressed: it queues a real archive of everything the workspace has.
    onSettings("export-request", "settings-export-request", "mdi-tray-arrow-down", {
      placement: "bottom",
      scene: "tabs",
      permissions: EXPORT_DATA,
    }),
    onSettings("export-list", "settings-export-table", "mdi-table-clock", {
      placement: "top",
      scene: "tabs",
      permissions: EXPORT_DATA,
    }),

    // ── Where to find this again ─────────────────────────────────────────────
    // The trigger beside the page's own heading.
    onSettings("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Am Mitarbeiter ───────────────────────────────────────────────────────
    //
    // Only what the settings hand over to a single person: the page's tab strip, the role assignment
    // the default role is only a suggestion for, and the employer's contribution that overrides the
    // workspace's percentage. Both tabs are pressed where the reader can see it, one scene, so a jump
    // onto either step replays the tab press at speed first.
    onEmployee("employee-tabs", "employee-tabs", "mdi-tab", {
      placement: "bottom",
      scene: "employee-record",
      actions: [{ do: "openTab", target: "employee-tab-system" }],
    }),
    // The tab exists for a login user; a data user has no roles, and the step then drops out.
    onEmployee("employee-roles", "employee-roles", "mdi-shield-account-outline", {
      placement: "bottom",
      scene: "employee-record",
      at: { params: { employeeId: "demo-1" }, hash: "system" },
    }),
    // Further down the company tab, just above the card's sticky save bar — which `visibleView`
    // counts as covering the scroller's bottom edge, so the field comes to rest above it.
    onEmployee("employee-contribution", "employee-contribution", "mdi-percent-outline", {
      placement: "top",
      scene: "employee-record",
      at: { params: { employeeId: "demo-1" }, hash: "company" },
      actions: [{ do: "openTab", target: "employee-tab-company" }],
    }),

    // ── Wo eine Vorgabe ankommt ──────────────────────────────────────────────
    //
    // Three pages that read a setting made here, one sentence each from the settings' side. The
    // Kalkulation card of a project's running calculation is where the employer's contribution
    // becomes money ("inkl. Arbeitgeberanteil" under the cost to date, `LewProjectCostPlanEditor`);
    // the backend grosses every labour hour up by the per-employee percent or the tenant's
    // (`FrontendProjectCostConverter.employerPercent`).
    onCosts("costs-contribution", "costs-plan", "mdi-percent-outline", { placement: "bottom" }),
    // The week Meine Zeiten shows starts on the tenant's first day of the week (`useWeekStart`, read
    // by `startOfWeek` in `TimeTrackingMy`), and the tiles beside it count exactly those days.
    // Above the row, not below it: the row runs the full width, so the camera cannot zoom, and the
    // text placed under it would cover the very tiles the step says count those seven days.
    onMyTimes("times-weekstart", "tt-my-week", "mdi-calendar-week", { placement: "top" }),
    // The employee lane of the board, where an absence is drawn in the colour of its kind — the
    // colour picked on the Abwesenheitsarten tab (`employeeDataGetters`, absence before project).
    // Below the lane, so the text does not sit over the very bars it is about.
    onPlanning("planning-absence", "planning-section-employees", "mdi-calendar-remove-outline", {
      placement: "bottom",
      scene: "planning-board",
    }),
  ],
}
